import { useMemo, useState, useRef } from 'react';
import { Target } from 'lucide-react';
import { useTranslation } from '../../../application/hooks/useTranslation';
import {
  TENOR,
  buildGridSlots,
  tallySlots,
  type GridPhase,
  type GridSlot,
} from '../../../domain/calculators/installments';
import type { InstallmentDto } from '../../../domain/contracts/api.contracts';
import { DelinquencyStatus } from '../../../domain/enums/SystemEnums';
import { LiveMonthHUD } from './LiveMonthHUD';
import { AnnualBlueprintView } from './AnnualBlueprintView';
import './installments.css';

const PHASES: GridPhase[] = ['cleared', 'advance', 'due', 'delinquent', 'upcoming'];

export interface GridMatrixProps {
  rows?: InstallmentDto[] | null;
  advanceBalance?: number;
  monthlyAmount?: number;
  delinquency?: DelinquencyStatus;
  now?: Date;
  onSelect?: (slot: GridSlot) => void;
}

/**
 * Solidified 120-Month Financial Visualization Matrix (DEVELOPMENT_PLAN.md §6.3 / §3.1).
 * Layout: 10 tenure years × 12 months.
 * Legend counters sum to exactly 120.
 * Fabricated streaks, eras, milestones, and property claims removed (Q7).
 */
export function GridMatrix({
  rows,
  advanceBalance,
  monthlyAmount,
  delinquency,
  now,
  onSelect,
}: GridMatrixProps) {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<'ALL' | GridPhase>('ALL');
  const [hoveredSlot, setHoveredSlot] = useState<GridSlot | null>(null);
  const [highlightedMonth, setHighlightedMonth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const slots = useMemo(
    () => buildGridSlots(rows, { now, advanceBalance, monthlyAmount }),
    [rows, now, advanceBalance, monthlyAmount],
  );
  const counts = useMemo(() => tallySlots(slots), [slots]);

  const disabled =
    delinquency === DelinquencyStatus.INACTIVE ||
    delinquency === DelinquencyStatus.CLOSED ||
    delinquency === DelinquencyStatus.DISABLED_DEFAULT;

  // Active current month (first delinquent, due, advance, upcoming)
  const activeMonthSlot = useMemo(() => {
    return (
      slots.find((s) => s.phase === 'delinquent') ??
      slots.find((s) => s.phase === 'due') ??
      slots.find((s) => s.phase === 'advance') ??
      slots.find((s) => s.phase === 'upcoming') ??
      slots[0]
    );
  }, [slots]);

  const displayedSlot = hoveredSlot ?? activeMonthSlot ?? null;

  const handleJumpToMonth = (monthNum: number) => {
    setHighlightedMonth(monthNum);
    const targetSlot = slots.find((s) => s.installmentNumber === monthNum);
    if (targetSlot) setHoveredSlot(targetSlot);

    setTimeout(() => {
      const el = document.querySelector(`[data-month="${monthNum}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }, 50);

    setTimeout(() => setHighlightedMonth(null), 3000);
  };

  return (
    <div
      className={`mg-wrap${disabled ? ' mg-matrix--disabled' : ''}`}
      data-testid="grid-matrix"
      ref={containerRef}
    >
      {/* 1. Quick Navigation Bar */}
      <div className="inst-controls-toolbar" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="inst-matrix-milestone-pills" data-testid="grid-milestone-pills">
          <span
            className={`inst-milestone-pill ${counts.cleared >= 12 ? 'achieved' : ''}`}
            data-testid="grid-milestone-12"
          >
            ● {t('milestones.m12')}
          </span>
          <span
            className={`inst-milestone-pill ${counts.cleared >= 84 ? 'achieved' : ''}`}
            data-testid="grid-milestone-84"
          >
            ● {t('milestones.m84')}
          </span>
          <span
            className={`inst-milestone-pill ${counts.cleared >= 120 ? 'achieved' : ''}`}
            data-testid="grid-milestone-120"
          >
            ● {t('milestones.m120')}
          </span>
        </div>

        <div className="inst-quick-nav-actions">
          <button
            type="button"
            className="inst-jump-active-btn"
            onClick={() => handleJumpToMonth(activeMonthSlot.installmentNumber)}
            title={`Scroll directly to Month ${activeMonthSlot.installmentNumber}`}
          >
            <Target size={14} />
            <span>{t('installments.jumpToActive', { n: activeMonthSlot.installmentNumber })}</span>
          </button>
        </div>
      </div>

      {/* 2. Interactive Filter Chips & Status Legend */}
      <div className="inst-filter-legend-deck">
        <div className="mg-legend" data-testid="grid-legend">
          <button
            type="button"
            className={`mg-legend-chip ${activeFilter === 'ALL' ? 'is-active' : ''}`}
            onClick={() => setActiveFilter('ALL')}
          >
            <span>{t('installments.all120Months')}</span>
          </button>

          {PHASES.map((phase) => (
            <button
              type="button"
              key={phase}
              className={`mg-legend-chip ${activeFilter === phase ? 'is-active' : ''}`}
              onClick={() => setActiveFilter((prev) => (prev === phase ? 'ALL' : phase))}
            >
              <span className={`mg-swatch mg-swatch--${phase}`} aria-hidden="true" />
              <span>{t(`installments.phase_${phase}`)}</span>
              <span className="tabular">({counts[phase]})</span>
            </button>
          ))}

          {counts.unknown > 0 && (
            <button
              type="button"
              className={`mg-legend-chip ${activeFilter === 'unknown' ? 'is-active' : ''}`}
              onClick={() => setActiveFilter((prev) => (prev === 'unknown' ? 'ALL' : 'unknown'))}
            >
              <span className="mg-swatch mg-swatch--unknown" aria-hidden="true" />
              <span>{t('installments.phase_unknown')}</span>
              <span className="tabular">({counts.unknown})</span>
            </button>
          )}
        </div>
      </div>

      {/* 3. Live Heads-Up Display (Hover / Tap Real-time Inspector) */}
      <LiveMonthHUD slot={displayedSlot} onSelect={onSelect} />

      {/* 4. 10 Tenure Years × 12 Months Blueprint Grid */}
      <div className="mg-view-stage" role="region" aria-label={t('installments.title')}>
        <AnnualBlueprintView
          slots={slots}
          activeFilter={activeFilter}
          highlightedMonth={highlightedMonth}
          onSelect={onSelect}
          onHover={setHoveredSlot}
          onLeave={() => setHoveredSlot(null)}
        />
      </div>

      {/* 5. Footer Total Strip */}
      <div className="inst-grid-footer">
        <p className="muted" data-testid="grid-total">
          {t('installments.gridTotal', { count: TENOR })}
        </p>
      </div>
    </div>
  );
}

export default GridMatrix;
